/*
 * Licensed to the Apache Software Foundation (ASF) under one
 * or more contributor license agreements.  See the NOTICE file
 * distributed with this work for additional information
 * regarding copyright ownership.  The ASF licenses this file
 * to you under the Apache License, Version 2.0 (the
 * "License"); you may not use this file except in compliance
 * with the License.  You may obtain a copy of the License at
 *
 *   http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing,
 * software distributed under the License is distributed on an
 * "AS IS" BASIS, WITHOUT WARRANTIES OR CONDITIONS OF ANY
 * KIND, either express or implied.  See the License for the
 * specific language governing permissions and limitations
 * under the License.
 */

import { FC, MouseEvent, useEffect } from 'react';
import { Nav } from 'react-bootstrap';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { loggedUserInfoStore, sideNavStore } from '@/stores';
import { Icon, PluginRender } from '@/components';
import { PluginType } from '@/utils/pluginKit';
import {
  launchRelaySSO,
  relayPanelSSO,
  relayQuotaSSO,
  relayServiceSSO,
  relaySSOTargets,
} from '@/utils/relaySSO';
import request from '@/utils/request';
import { isEnterpriseMember } from '@/services/enterpriseAssociation';
import { useEnterpriseAssociation } from '@/hooks/useEnterpriseAssociation';

import './index.scss';

const openRelayWithAnswerToken = (
  event: MouseEvent<HTMLElement>,
  target: typeof relayPanelSSO,
) => {
  event.preventDefault();
  launchRelaySSO(target);
};

const Index: FC = () => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const { user: userInfo } = loggedUserInfoStore();
  const { can_revision, revision } = sideNavStore();
  const navigate = useNavigate();
  const { association } = useEnterpriseAssociation(Boolean(userInfo?.id));
  const enterpriseBound = isEnterpriseMember(association);

  useEffect(() => {
    if (!userInfo?.id) {
      return;
    }
    const currentURL = new URL(window.location.href);
    const relayKey = currentURL.searchParams.get('relay_sso');
    if (!relayKey || !(relayKey in relaySSOTargets)) {
      return;
    }
    const target = relaySSOTargets[relayKey as keyof typeof relaySSOTargets];
    const forceSessionRecovery =
      currentURL.searchParams.get('relay_reauth') === '1';
    currentURL.searchParams.delete('relay_sso');
    currentURL.searchParams.delete('relay_reauth');
    window.history.replaceState(
      window.history.state,
      document.title,
      `${currentURL.pathname}${currentURL.search}${currentURL.hash}`,
    );
    if (target.adminOnly && userInfo.role_id !== 2) {
      return;
    }
    launchRelaySSO(target, {
      allowCanonicalResume: false,
      forceSessionRecovery,
    });
  }, [userInfo?.id, userInfo?.role_id]);

  return (
    <Nav variant="pills" className="flex-column" id="sideNav">
      <Nav.Link href="https://aiopentool.com" className="border rounded-2 mb-2">
        <Icon name="box-arrow-up-right" className="me-2" />
        <span>Codex汉化增强版</span>
      </Nav.Link>

      <NavLink
        to="/questions"
        className={({ isActive }) =>
          isActive || pathname === '/' ? 'nav-link active' : 'nav-link'
        }>
        <Icon name="question-circle-fill" className="me-2" />
        <span>{t('header.nav.question')}</span>
      </NavLink>

      <NavLink
        to="/downloads"
        className={() =>
          pathname === '/downloads' ? 'nav-link active' : 'nav-link'
        }>
        <Icon name="download" className="me-2" />
        <span>下载</span>
      </NavLink>

      {userInfo?.id && (
        <NavLink
          to="/mobile-skins"
          className={() =>
            pathname === '/mobile-skins' ? 'nav-link active' : 'nav-link'
          }>
          <Icon name="palette" className="me-2" />
          <span>分享市场</span>
        </NavLink>
      )}

      {userInfo?.id && userInfo.welfare_entry_visible === true && (
        <NavLink
          to="/welfare"
          className={() =>
            pathname === '/welfare' ? 'nav-link active' : 'nav-link'
          }>
          <Icon name="gift-fill" className="me-2" />
          <span>福利</span>
        </NavLink>
      )}

      {enterpriseBound && (
        <Nav.Link
          href="https://s.aiopentool.com/sso/start"
          rel="nofollow noreferrer"
          data-nosnippet
          className="nav-link">
          <Icon name="building" className="me-2" />
          <span>企业服务</span>
        </Nav.Link>
      )}

      {userInfo?.id && userInfo.welfare_entry_visible === true && (
        <Nav.Link
          href={relayPanelSSO.fallbackURL}
          onClick={(event) => openRelayWithAnswerToken(event, relayPanelSSO)}
          rel="nofollow noreferrer"
          data-nosnippet
          className="nav-link">
          <Icon name="diagram-3-fill" className="me-2" />
          <span>福利中转</span>
        </Nav.Link>
      )}

      {userInfo?.id && (
        <NavLink
          to="/terminals"
          className={() =>
            pathname === '/terminals' ? 'nav-link active' : 'nav-link'
          }>
          <Icon name="pc-display" className="me-2" />
          <span>我的终端</span>
        </NavLink>
      )}

      <NavLink
        to="/ai-merchants"
        className={() =>
          pathname.startsWith('/ai-merchants') ? 'nav-link active' : 'nav-link'
        }>
        <Icon name="shop" className="me-2" />
        <span>AI服务商</span>
      </NavLink>

      <NavLink
        to="/tags"
        className={() =>
          pathname === '/tags' ? 'nav-link active' : 'nav-link'
        }>
        <Icon name="tags-fill" className="me-2" />
        <span>{t('header.nav.tag')}</span>
      </NavLink>

      <NavLink to="/users" className="nav-link">
        <Icon name="people-fill" className="me-2" />
        <span>{t('header.nav.user')}</span>
      </NavLink>

      <NavLink to="/badges" className="nav-link">
        <Icon name="award-fill" className="me-2" />
        <span>{t('header.nav.badges')}</span>
      </NavLink>

      <PluginRender
        slug_name="quick_links"
        type={PluginType.Sidebar}
        request={request}
        navigate={navigate}
      />

      {can_revision || userInfo?.role_id === 2 ? (
        <>
          <div className="py-2 px-3 mt-3 small fw-bold">
            {t('header.nav.moderation')}
          </div>
          {can_revision && (
            <NavLink to="/review" className="nav-link">
              <Icon name="shield-fill-check" className="me-2" />
              <span>{t('header.nav.review')}</span>
              <span className="float-end">
                {revision > 99 ? '99+' : revision > 0 ? revision : ''}
              </span>
            </NavLink>
          )}

          {userInfo?.role_id === 2 ? (
            <>
              <NavLink to="/admin" className="nav-link">
                <Icon name="gear-fill" className="me-2" />
                <span>{t('header.nav.admin')}</span>
              </NavLink>
              <Nav.Link
                href={relayPanelSSO.fallbackURL}
                onClick={(event) =>
                  openRelayWithAnswerToken(event, relayPanelSSO)
                }
                className="nav-link">
                <Icon name="diagram-3-fill" className="me-2" />
                <span>中转站用户管理</span>
              </Nav.Link>
              <Nav.Link
                href={relayQuotaSSO.fallbackURL}
                onClick={(event) =>
                  openRelayWithAnswerToken(event, relayQuotaSSO)
                }
                className="nav-link">
                <Icon name="speedometer2" className="me-2" />
                <span>中转站额度巡检</span>
              </Nav.Link>
              <Nav.Link
                href={relayServiceSSO.fallbackURL}
                onClick={(event) =>
                  openRelayWithAnswerToken(event, relayServiceSSO)
                }
                className="nav-link">
                <Icon name="hdd-network-fill" className="me-2" />
                <span>中转站服务管理</span>
              </Nav.Link>
            </>
          ) : null}
        </>
      ) : null}
    </Nav>
  );
};

export default Index;
